import React from 'react';
import { useSEO } from '../hooks/useSEO';
import { Link } from 'react-router-dom';
import PageHero from '../components/PageHero';
import { SCHOOL_GALLERY } from '../data/mock';
import { ArrowUpRight } from 'lucide-react';
import { EditRegion } from '../components/AdminEditBar';
import { InlineText, InlineImage, useEditMode } from '../components/InlineEditable';
import { usePublicSettings } from '../hooks/usePublicSettings';

// Default stat rows (used as InlineText fallbacks so admin can edit each).
const DEFAULT_STATS = [
  { label: 'Konum', value: 'Ayazağa, Sarıyer / İSTANBUL' },
  { label: 'Öğrenci sayısı', value: '720+ aktif öğrenci' },
  { label: 'Öğretmen kadrosu', value: '48 öğretmen' },
  { label: 'Hizmet süresi', value: "2003'ten beri hizmette" },
];

export default function School() {
  useSEO('school');
  const settings = usePublicSettings();
  const canEdit = useEditMode();

  return (
    <EditRegion label="YEKÜV Okulu" to="/admin/static-pages">
      <>
      <PageHero slug="school"
        kicker="YEKÜV Nilüfer Gökay Ortaokulu"
        title={<>Kurucu başkanımızın <span className="italic text-coral-200">adını taşıyan</span> okulumuz.</>}
        text="Ayazağa'da bulunan YEKÜV Nilüfer Gökay Ortaokulu, vakfımızın eğitim alanında attığı en somut adımlardan biridir."
      />
      <section className="bg-cream">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 md:py-28">
          <div className="grid lg:grid-cols-12 gap-10">
            {/* Left — Gallery (4 images, each individually editable) */}
            <div className="lg:col-span-7">
              <div className="rounded-3xl overflow-hidden aspect-[16/10] bg-ink-50">
                <InlineImage
                  settingKey="school_image_1"
                  fallback={SCHOOL_GALLERY[0]}
                  alt="Okul ana görsel"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="grid grid-cols-3 gap-3 mt-3">
                {[1, 2, 3].map((idx) => (
                  <div key={idx} className="rounded-2xl overflow-hidden aspect-[4/3] bg-ink-50">
                    <InlineImage
                      settingKey={`school_image_${idx + 1}`}
                      fallback={SCHOOL_GALLERY[idx]}
                      alt={`Okul galeri ${idx + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Right — Content (each block individually editable) */}
            <div className="lg:col-span-5">
              <InlineText
                settingKey="school_kicker"
                fallback="Okul Hakkında"
                as="div"
                className="text-[11px] tracking-[0.3em] uppercase text-coral-300 font-semibold"
              />
              <InlineText
                settingKey="school_title"
                fallback="Eğitimde örnek bir model"
                as="h2"
                multiline
                className="font-display text-4xl md:text-5xl mt-3 text-ink-600 leading-[1.05]"
              />
              <InlineText
                settingKey="school_intro"
                fallback="YEKÜV Nilüfer Gökay Ortaokulu, İstanbul Sarıyer / Ayazağa'da 2003 yılında hizmete girdi. Vakfımızın bağlı olduğu MEB müfredatını destekleyici kültür-sanat ve sosyal programımızla birlikte, öğrencilerimize bütünsel bir eğitim deneyimi sunar."
                as="p"
                multiline
                className="mt-5 text-ink-500 leading-relaxed"
              />

              {/* Stats — rows whose label & value are blank (or just punctuation) are hidden on the public site */}
              <ul className="mt-6 space-y-3" data-testid="school-stats">
                {DEFAULT_STATS.map((row, i) => {
                  const n = i + 1;
                  const label = settings[`school_stat_${n}_label`] ?? row.label;
                  const value = settings[`school_stat_${n}_value`] ?? row.value;
                  const isBlank = (s) => !String(s ?? '').replace(/[.\s:•·,;-]/g, '');
                  if (!canEdit && isBlank(label) && isBlank(value)) return null;
                  return (
                    <li key={n} className="flex items-baseline gap-2 flex-wrap">
                      <span className="w-1.5 h-1.5 rounded-full bg-coral-300" />
                      <InlineText
                        settingKey={`school_stat_${n}_label`}
                        fallback={row.label}
                        as="span"
                        className="font-semibold text-ink-600"
                      />
                      <span className="text-ink-400">:</span>
                      <InlineText
                        settingKey={`school_stat_${n}_value`}
                        fallback={row.value}
                        as="span"
                        multiline
                        className="text-ink-500 flex-1 min-w-0"
                      />
                    </li>
                  );
                })}
              </ul>

              {/* Founder */}
              <div className="mt-8 pt-6 border-t border-ink-100">
                <InlineText
                  settingKey="school_founder_kicker"
                  fallback="Kurucu Adı"
                  as="h3"
                  className="font-display text-2xl text-ink-600"
                />
                <InlineText
                  settingKey="school_founder_text"
                  fallback="Nilüfer Gökay — Vakfın kurucularından olan, temel taşı bağışçılarımızın önde gelen isimlerinden biri olan Gökay'ın ismini eğitimle yaşatmaktan mutluluk duymaktayız."
                  as="p"
                  multiline
                  html
                  className="mt-2 text-ink-500 leading-relaxed"
                />
              </div>

              <Link to="/iletisim" className="mt-8 inline-flex items-center gap-2 bg-ink-600 text-cream rounded-full px-6 h-12 font-medium hover:bg-ink-500">
                İletişim & Ziyaret <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
    </EditRegion>
  );
}
